import React, { useState, useEffect } from 'react';
import { Users, Activity, MessageSquare, CheckSquare, Calendar, Bell, Settings, Brain } from 'lucide-react';
import { adminService } from '../services/adminService';
import Navigation from '../components/Navigation';
import MemoryWikiBrowser from '../components/admin/MemoryWikiBrowser';

const AdminPanel = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [activeTab, setActiveTab] = useState('engagement');
  const [userAnalyses, setUserAnalyses] = useState({});
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState('not_started');
  const [message, setMessage] = useState('');
  const [editableMessage, setEditableMessage] = useState('');
  const [pollingInterval, setPollingInterval] = useState(null);
  const [tabData, setTabData] = useState({});

  // Conversations pagination & search state
  const [convSearch, setConvSearch] = useState('');
  const [convLastKey, setConvLastKey] = useState(null);
  const [convHasMore, setConvHasMore] = useState(false);
  const [convLoading, setConvLoading] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  // Load analyses when user is selected (only if not already loaded)
  useEffect(() => {
    if (selectedUserId && !userAnalyses[selectedUserId]) {
      loadUserAnalyses(selectedUserId);
    }
  }, [selectedUserId]);

  // Load tab data when user or tab changes
  useEffect(() => {
    if (selectedUserId && activeTab !== 'engagement') {
      loadTabData(selectedUserId, activeTab);
    }
  }, [selectedUserId, activeTab]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const checkAdminAccess = async () => {
    try {
      const response = await adminService.getUsers();
      setIsAdmin(true);
      setUsers(response.users);
    } catch (error) {
      console.error('Admin access denied:', error);
      setMessage('Access denied. Admin privileges required.');
    }
  };

  const loadUserAnalyses = async (userId) => {
    try {
      console.log('Loading analyses for user:', userId);
      const response = await adminService.getUserAnalyses(userId);
      console.log('Loaded analyses:', response);
      setUserAnalyses(prev => ({ ...prev, [userId]: response.analyses }));
      
      // Set current analysis to the latest one
      if (response.analyses && response.analyses.length > 0) {
        setCurrentAnalysis(response.analyses[0]);
        if (response.analyses[0].status === 'done') {
          setEditableMessage(response.analyses[0].analysis_text);
        }
      }
    } catch (error) {
      console.error('Error loading analyses:', error);
    }
  };

  const loadTabData = async (userId, tab) => {
    if (tab === 'conversations') {
      await loadConversations(userId, null, true);
      return;
    }
    if (tab === 'memory') {
      return; // Memory tab handles its own data loading
    }
    try {
      const data = await adminService.getUserTabData(userId, tab);
      setTabData(prev => ({ ...prev, [userId]: { ...prev[userId], [tab]: data } }));
    } catch (error) {
      console.error(`Error loading ${tab} data:`, error);
      setMessage(`Error loading ${tab} data: ${error.message}`);
    }
  };

  const loadConversations = async (userId, lastKey = null, reset = false) => {
    setConvLoading(true);
    try {
      const params = { page_size: 20 };
      if (lastKey) params.last_key = lastKey;

      const data = await adminService.getUserTabData(userId, 'conversations', params);

      setTabData(prev => {
        const existing = reset ? [] : (prev[userId]?.conversations?.conversations || []);
        return {
          ...prev,
          [userId]: {
            ...prev[userId],
            conversations: {
              conversations: [...existing, ...data.conversations],
              last_key: data.last_key,
              has_more: data.has_more
            }
          }
        };
      });
      setConvLastKey(data.last_key);
      setConvHasMore(data.has_more);
    } catch (error) {
      console.error('Error loading conversations:', error);
      setMessage(`Error loading conversations: ${error.message}`);
    } finally {
      setConvLoading(false);
    }
  };

  const analyzeUser = async (userId) => {
    try {
      setAnalysisStatus('running');
      setMessage('Starting engagement analysis...');
      setCurrentAnalysis(null);
      
      // Start analysis
      const response = await adminService.analyzeEngagement(userId);
      const analysisId = response.analysis_id;
      
      setMessage(`Analysis started! Click refresh to check status.`);
      setAnalysisStatus('waiting');
      
      // Reload analyses to show the new one
      await loadUserAnalyses(userId);
      
    } catch (error) {
      console.error('Error analyzing user:', error);
      setAnalysisStatus('not_started');
      setMessage('Error starting analysis: ' + (error.response?.data?.detail || error.message));
    }
  };

  const refreshAnalyses = async () => {
    if (!selectedUserId) return;
    
    setMessage('Refreshing analyses...');
    await loadUserAnalyses(selectedUserId);
    setMessage('Analyses refreshed');
    
    // If we have a current analysis that was generating, check if it's done now
    if (currentAnalysis && currentAnalysis.status === 'generating') {
      const updated = analyses.find(a => a.analysis_id === currentAnalysis.analysis_id);
      if (updated) {
        setCurrentAnalysis(updated);
        if (updated.status === 'done') {
          setEditableMessage(updated.analysis_text);
          setAnalysisStatus('completed');
        }
      }
    }
  };

  const sendMessage = async (userId, messageText) => {
    try {
      setMessage('Sending message...');
      await adminService.sendMessage(userId, messageText);
      setMessage('Message sent successfully');
    } catch (error) {
      console.error('Error sending message:', error);
      // Extract error detail from API response
      const errorDetail = error.response?.data?.detail || error.message || 'Unknown error';
      setMessage(`Error: ${errorDetail}`);
    }
  };

  const selectedUser = users.find(u => u.user_id === selectedUserId);
  const analyses = selectedUserId ? (userAnalyses[selectedUserId] || []) : [];
  const currentTabData = selectedUserId && tabData[selectedUserId] ? tabData[selectedUserId][activeTab] : null;

  const renderTabContent = () => {
    if (!selectedUser) {
      return (
        <div style={{
          textAlign: 'center',
          color: '#9ca3af',
          paddingTop: '120px'
        }}>
          <Users size={64} style={{margin: '0 auto 1rem', opacity: 0.2}} />
          <p style={{fontSize: '1.125rem'}}>Select a user to view details</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'engagement':
        return renderEngagementTab();
      case 'conversations':
        return renderConversationsTab();
      case 'tasks':
        return renderTasksTab();
      case 'calendar':
        return renderCalendarTab();
      case 'reminders':
        return renderRemindersTab();
      case 'config':
        return renderConfigTab();
      case 'memory':
        return renderMemoryTab();
      default:
        return null;
    }
  };

  const renderEngagementTab = () => (
    <>
      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <button
          onClick={() => analyzeUser(selectedUserId)}
          disabled={analysisStatus === 'running'}
          style={{
            flex: 1,
            padding: '0.75rem',
            background: analysisStatus === 'running' ? '#9ca3af' : '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: analysisStatus === 'running' ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s'
          }}
        >
          {analysisStatus === 'running' ? '🔄 Starting...' : '🤖 Analyze Engagement'}
        </button>
        
        <button
          onClick={refreshAnalyses}
          style={{
            padding: '0.75rem 1rem',
            background: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
        >
          🔄 Refresh
        </button>
        
        <button
          onClick={() => {
            setAnalysisStatus('send_message');
            setEditableMessage('');
          }}
          style={{
            flex: 1,
            padding: '0.75rem',
            background: '#059669',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
        >
          📤 Send Message
        </button>
      </div>

      {/* Analysis History Navigation */}
      {analyses && analyses.length > 0 && (
        <div style={{ 
          marginBottom: '1.5rem',
          padding: '1rem',
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '0.75rem'
          }}>
            📊 Analysis History ({analyses.length}):
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {analyses.map((analysis) => (
              <button
                key={analysis.analysis_id}
                onClick={() => {
                  console.log('Switching to analysis:', analysis);
                  setCurrentAnalysis(analysis);
                  if (analysis.status === 'done') {
                    setEditableMessage(analysis.analysis_text);
                    setAnalysisStatus('completed');
                  }
                }}
                style={{
                  padding: '0.5rem 0.75rem',
                  background: currentAnalysis?.analysis_id === analysis.analysis_id ? '#2563eb' : '#f3f4f6',
                  color: currentAnalysis?.analysis_id === analysis.analysis_id ? 'white' : '#374151',
                  border: currentAnalysis?.analysis_id === analysis.analysis_id ? '2px solid #1e40af' : '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                {analysis.status === 'generating' && '⏳'}
                {analysis.status === 'done' && '✅'}
                {analysis.status === 'failed' && '❌'}
                <span>{new Date(analysis.timestamp).toLocaleString()}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '1.5rem',
        minHeight: '300px'
      }}>
        {analysisStatus === 'not_started' && !currentAnalysis && (
          <div style={{
            textAlign: 'center',
            color: '#6b7280',
            paddingTop: '80px'
          }}>
            <Activity size={48} style={{margin: '0 auto 1rem', opacity: 0.3}} />
            <p>Click "Analyze Engagement" for AI analysis or "Send Message" for ad-hoc messaging</p>
          </div>
        )}

        {analysisStatus === 'running' && (
          <div style={{
            textAlign: 'center',
            color: '#2563eb',
            paddingTop: '40px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #e5e7eb',
              borderTopColor: '#2563eb',
              borderRadius: '50%',
              margin: '0 auto 1rem',
              animation: 'spin 1s linear infinite'
            }} />
            <p style={{fontWeight: '600'}}>Running AI analysis...</p>
          </div>
        )}

        {(analysisStatus === 'completed' || (currentAnalysis && currentAnalysis.status === 'done')) && (
          <>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#059669',
              fontWeight: '600',
              marginBottom: '1rem'
            }}>
              <span>✅</span>
              <span>Analysis Complete</span>
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                AI Engagement Analysis:
              </label>
              <div style={{
                background: '#f9fafb',
                padding: '1rem',
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {currentAnalysis?.analysis_text || ''}
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Message to Send:
              </label>
              <textarea
                value={editableMessage}
                onChange={(e) => setEditableMessage(e.target.value)}
                placeholder="Edit the message before sending..."
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  lineHeight: '1.5',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <button
              onClick={() => sendMessage(selectedUserId, editableMessage)}
              disabled={!editableMessage.trim()}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: editableMessage.trim() ? '#059669' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: editableMessage.trim() ? 'pointer' : 'not-allowed',
                transition: 'background 0.2s'
              }}
            >
              📤 Send Engagement Message
            </button>
          </>
        )}

        {analysisStatus === 'send_message' && (
          <>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#059669',
              fontWeight: '600',
              marginBottom: '1rem'
            }}>
              <span>📤</span>
              <span>Send Ad-hoc Message</span>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Message:
              </label>
              <textarea
                value={editableMessage}
                onChange={(e) => setEditableMessage(e.target.value)}
                placeholder="Type your message here..."
                style={{
                  width: '100%',
                  minHeight: '200px',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  lineHeight: '1.5',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <button
              onClick={() => sendMessage(selectedUserId, editableMessage)}
              disabled={!editableMessage.trim()}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: editableMessage.trim() ? '#059669' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: editableMessage.trim() ? 'pointer' : 'not-allowed',
                transition: 'background 0.2s'
              }}
            >
              📤 Send Message
            </button>
          </>
        )}
      </div>
    </>
  );

  const renderConversationsTab = () => {
    const allConversations = currentTabData?.conversations || [];
    const searchLower = convSearch.toLowerCase();
    const filtered = convSearch
      ? allConversations.filter(c =>
          (c.message && c.message.toLowerCase().includes(searchLower)) ||
          (c.response && c.response.toLowerCase().includes(searchLower))
        )
      : allConversations;

    const highlight = (text) => {
      if (!convSearch || !text) return text;
      const parts = text.split(new RegExp(`(${convSearch})`, 'gi'));
      return parts.map((part, i) =>
        part.toLowerCase() === searchLower
          ? <mark key={i} style={{ background: '#fef08a', padding: 0 }}>{part}</mark>
          : part
      );
    };

    return (
      <div style={{ padding: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
            💬 Conversation History
            {allConversations.length > 0 && (
              <span style={{ fontSize: '0.8rem', fontWeight: '400', color: '#6b7280', marginLeft: '0.5rem' }}>
                ({filtered.length}{convSearch ? ` de ${allConversations.length}` : ''} mensagens)
              </span>
            )}
          </h3>
        </div>

        {/* Search input */}
        <div style={{ marginBottom: '1rem', position: 'relative' }}>
          <input
            type="text"
            value={convSearch}
            onChange={e => setConvSearch(e.target.value)}
            placeholder="Buscar por palavra-chave..."
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem 0.5rem 2.25rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.875rem',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          <span style={{ position: 'absolute', left: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '1rem' }}>
            🔍
          </span>
          {convSearch && (
            <button
              onClick={() => setConvSearch('')}
              style={{
                position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '1rem'
              }}
            >
              ✕
            </button>
          )}
        </div>

        {!currentTabData ? (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
            Carregando conversas...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
            {convSearch ? 'Nenhuma conversa encontrada para esta busca.' : 'Nenhuma conversa encontrada.'}
          </div>
        ) : (
          <div style={{ maxHeight: '520px', overflowY: 'auto' }}>
            {filtered.map((conv, idx) => (
              <div key={conv.message_id || idx} style={{
                padding: '1rem',
                marginBottom: '0.75rem',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                  {new Date(conv.timestamp).toLocaleString()}
                  {conv.source && (
                    <span style={{
                      marginLeft: '0.5rem',
                      padding: '0.1rem 0.4rem',
                      background: '#f3f4f6',
                      borderRadius: '4px',
                      fontSize: '0.7rem'
                    }}>
                      {conv.source}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.875rem', lineHeight: '1.5', marginBottom: '0.5rem' }}>
                  <strong>User:</strong> {highlight(conv.message)}
                </div>
                {conv.response && (
                  <div style={{ fontSize: '0.875rem', lineHeight: '1.5', color: '#374151' }}>
                    <strong>Assistant:</strong> {highlight(conv.response)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Load More button */}
        {!convSearch && (
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            {convHasMore ? (
              <button
                onClick={() => loadConversations(selectedUserId, convLastKey, false)}
                disabled={convLoading}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: convLoading ? '#9ca3af' : '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: convLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {convLoading ? 'Carregando...' : '⬆ Carregar mensagens mais antigas'}
              </button>
            ) : allConversations.length > 0 ? (
              <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                Todas as mensagens carregadas ({allConversations.length} no total)
              </span>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  const renderTasksTab = () => (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginBottom: '1rem', fontSize: '1.125rem', fontWeight: '600' }}>
        ✅ Tasks
      </h3>
      {currentTabData ? (
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {currentTabData.tasks?.map((task, idx) => (
            <div key={idx} style={{
              padding: '1rem',
              marginBottom: '0.75rem',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{task.title}</span>
                <span style={{
                  padding: '0.125rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  background: task.status === 'done' ? '#dcfce7' : '#dbeafe',
                  color: task.status === 'done' ? '#166534' : '#1e40af'
                }}>
                  {task.status}
                </span>
              </div>
              {task.description && (
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                  {task.description}
                </div>
              )}
              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                {task.category && `Category: ${task.category}`}
                {task.due_date && ` • Due: ${new Date(task.due_date).toLocaleDateString()}`}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
          Loading tasks...
        </div>
      )}
    </div>
  );

  const renderCalendarTab = () => (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginBottom: '1rem', fontSize: '1.125rem', fontWeight: '600' }}>
        📅 Calendar Events
      </h3>
      {currentTabData ? (
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {currentTabData.events?.map((event, idx) => (
            <div key={idx} style={{
              padding: '1rem',
              marginBottom: '0.75rem',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: '600', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                {event.title}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                {event.description}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                {new Date(event.start_time).toLocaleString()} - {new Date(event.end_time).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
          Loading calendar events...
        </div>
      )}
    </div>
  );

  const renderRemindersTab = () => (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginBottom: '1rem', fontSize: '1.125rem', fontWeight: '600' }}>
        🔔 Reminders
      </h3>
      {currentTabData ? (
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {currentTabData.reminders?.map((reminder, idx) => (
            <div key={idx} style={{
              padding: '1rem',
              marginBottom: '0.75rem',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: '600', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                {reminder.message}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                Scheduled: {new Date(reminder.scheduled_time).toLocaleString()}
                {reminder.status && ` • Status: ${reminder.status}`}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
          Loading reminders...
        </div>
      )}
    </div>
  );

  const renderConfigTab = () => (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginBottom: '1rem', fontSize: '1.125rem', fontWeight: '600' }}>
        ⚙️ User Configuration
      </h3>
      {currentTabData ? (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '1.5rem'
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
              User ID:
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', fontFamily: 'monospace' }}>
              {selectedUser.user_id}
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
              Platform:
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {selectedUser.platform_user_id || 'Not configured'}
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
              Preferences:
            </div>
            <pre style={{
              fontSize: '0.75rem',
              color: '#6b7280',
              background: '#f9fafb',
              padding: '1rem',
              borderRadius: '6px',
              overflow: 'auto',
              maxHeight: '300px'
            }}>
              {JSON.stringify(currentTabData.preferences || selectedUser.preferences || {}, null, 2)}
            </pre>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
          Loading configuration...
        </div>
      )}
    </div>
  );

  const renderMemoryTab = () => (
    <MemoryWikiBrowser selectedUserId={selectedUserId} />
  );

  if (!isAdmin) {
    return (
      <>
        <Navigation />
        <div className="main-content">
          <div style={{
            maxWidth: '600px',
            margin: '100px auto',
            padding: '40px',
            textAlign: 'center',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{color: '#dc2626', marginBottom: '20px'}}>Access Denied</h2>
            <p style={{color: '#6b7280'}}>{message || 'Checking admin access...'}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="main-content" style={{padding: '2rem'}}>
        <div style={{maxWidth: '1400px', margin: '0 auto'}}>
          {/* Header */}
          <div style={{marginBottom: '2rem'}}>
            <h1 style={{fontSize: '2rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem'}}>
              Admin Panel
            </h1>
            <p style={{color: '#6b7280'}}>User Engagement Analysis & Management</p>
          </div>

          {/* Message Banner */}
          {message && (
            <div style={{
              padding: '1rem',
              marginBottom: '1.5rem',
              borderRadius: '8px',
              background: message.includes('Error') ? '#fee2e2' : '#d1fae5',
              color: message.includes('Error') ? '#991b1b' : '#065f46',
              border: `1px solid ${message.includes('Error') ? '#fecaca' : '#a7f3d0'}`
            }}>
              {message}
            </div>
          )}

          {/* Two Column Layout */}
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem'}}>
            {/* Users List */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              padding: '1.5rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1.5rem'
              }}>
                <h2 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#111827',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Users size={20} />
                  Users ({users.length})
                </h2>
              </div>

              <div style={{
                maxHeight: '600px',
                overflowY: 'auto'
              }}>
                {users.map(user => (
                  <div
                    key={user.user_id}
                    onClick={() => {
                      // Clear any ongoing polling
                      if (pollingInterval) {
                        clearInterval(pollingInterval);
                        setPollingInterval(null);
                      }
                      setSelectedUserId(user.user_id);
                      setAnalysisStatus('not_started');
                      setEditableMessage('');
                      setCurrentAnalysis(null);
                      setActiveTab('engagement');
                      setConvSearch('');
                      setConvLastKey(null);
                      setConvHasMore(false);
                    }}
                    style={{
                      padding: '1rem',
                      marginBottom: '0.75rem',
                      border: selectedUserId === user.user_id ? '2px solid #2563eb' : '1px solid #e5e7eb',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: selectedUserId === user.user_id ? '#eff6ff' : 'white',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{fontWeight: '600', color: '#111827', marginBottom: '0.25rem'}}>
                      {user.first_name} {user.last_name}
                    </div>
                    <div style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem'}}>
                      {user.email}
                    </div>
                    <div style={{fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem', fontFamily: 'monospace'}}>
                      ID: {user.user_id}
                    </div>
                    {user.platform_user_id && (
                      <div style={{fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem', fontFamily: 'monospace'}}>
                        {user.platform_user_id}
                      </div>
                    )}
                    <div style={{display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#9ca3af', alignItems: 'center'}}>
                      <span>Tasks: {user.task_count || 0}</span>
                      <span>Last Active: {user.last_activity || 'Never'}</span>
                      {user.platform_user_id ? (
                        <span style={{
                          background: user.platform_user_id.startsWith('whatsapp_') ? '#dcfce7' : '#dbeafe',
                          color: user.platform_user_id.startsWith('whatsapp_') ? '#166534' : '#1e40af',
                          padding: '0.125rem 0.5rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          {user.platform_user_id.startsWith('whatsapp_') ? '📱 WhatsApp' : '✈️ Telegram'}
                        </span>
                      ) : (
                        <span style={{
                          background: '#fee2e2',
                          color: '#991b1b',
                          padding: '0.125rem 0.5rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          ⚠️ No Platform
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Analysis Panel */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              padding: '1.5rem'
            }}>
              {selectedUser ? (
                <>
                  <h2 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: '#111827',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Activity size={20} />
                    {selectedUser.first_name} {selectedUser.last_name}
                  </h2>

                  {/* Tab Navigation */}
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginBottom: '1.5rem',
                    borderBottom: '2px solid #e5e7eb',
                    overflowX: 'auto'
                  }}>
                    {[
                      { id: 'engagement', label: 'Engagement', icon: Activity },
                      { id: 'conversations', label: 'Conversations', icon: MessageSquare },
                      { id: 'tasks', label: 'Tasks', icon: CheckSquare },
                      { id: 'calendar', label: 'Calendar', icon: Calendar },
                      { id: 'reminders', label: 'Reminders', icon: Bell },
                      { id: 'config', label: 'Config', icon: Settings },
                      { id: 'memory', label: 'Memory', icon: Brain }
                    ].map(tab => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          style={{
                            padding: '0.75rem 1rem',
                            background: 'transparent',
                            border: 'none',
                            borderBottom: activeTab === tab.id ? '2px solid #2563eb' : '2px solid transparent',
                            color: activeTab === tab.id ? '#2563eb' : '#6b7280',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            whiteSpace: 'nowrap',
                            marginBottom: '-2px'
                          }}
                        >
                          <Icon size={16} />
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Tab Content */}
                  {renderTabContent()}
                </>
              ) : (
                <div style={{
                  textAlign: 'center',
                  color: '#9ca3af',
                  paddingTop: '120px'
                }}>
                  <Users size={64} style={{margin: '0 auto 1rem', opacity: 0.2}} />
                  <p style={{fontSize: '1.125rem'}}>Select a user to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </>
  );
};

export default AdminPanel;
